import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { InfoCircleFilled, PlusOutlined } from "@ant-design/icons";
import {
  blankOnlineBuckets,
  hasAnyOnlinePricing,
  InPersonBucketState,
  listedOnlineGroupKeysForState,
  OnlinePricingState,
  OnlineValidationError,
  formatOnlineFee,
  savesOnlineVendorFee,
  unflooredOnlineGroups,
  validateOnlineState,
  withOnlineTemplateAxes,
} from "./partnerPricing";
import {
  ONLINE_TEMPLATE_CHOICES,
  OnlineTemplateChoice,
  onlineTemplateChoiceAxes,
  onlineTemplateChoiceFor,
  onlineTemplateChoiceLabel,
} from "./onlineTemplateChoice";
import { onlinePricingTemplateForCountry } from "../../../constants/onlineBuckets";
import {
  OnlineGroupKey,
  OnlinePricingStructure,
} from "../../../../../interfaces/onlinePricing.interface";
import { onlineGroupLabel } from "../../../../businesses/utils/sixteenBucketOnlinePricing";
import { resolveInvalidOnlinePricingBuckets } from "../../../../businesses/utils/onlinePricingSyncErrors";
import { OnlinePricingTemplate } from "../../../../../interfaces/partnerDeal.interface";
import { KlearlyCountryAlpha2Code } from "../../../../../interfaces/klearlyCountryAlpha2Code.enum";
import PricingBucketsTable, { PricingRow } from "./PricingBucketsTable";
import { WizardMode } from "../wizardSteps";
import { IOnlinePurchPriceProfile } from "../../../../../interfaces/partner.interface";

const { Text, Title } = Typography;

// A range created today prices the 16 buckets: it is the shape the merchant,
// deal and mapping surfaces are built around, and the four-bucket one is kept
// only for the ranges already stored on it. Deliberately not read off a sibling
// range — the structure is per range, and inheriting it is what left a partner
// whose ranges are all four-bucket with no way to be given a 16-bucket one.
const NEW_RANGE_STRUCTURE = OnlinePricingStructure.SixteenBucket;

const TEMPLATE_LOCKED_HINT =
  "This price range is used by a merchant submitted to Adyen, so its pricing template is fixed.";

// The one refusal that is about the template control rather than the buckets:
// it names the range and says the other structure needs one of its own.
const STRUCTURE_REFUSAL_CODE = "ONLINE_PRICING_STRUCTURE_CHANGE_NOT_ALLOWED";

interface Props {
  value: OnlinePricingState;
  onChange: (next: OnlinePricingState) => void;
  mode?: WizardMode;
  initial?: OnlinePricingState;
  profiles?: IOnlinePurchPriceProfile[];
  onProfileSelect?: (profileId: string) => void;
  onProfileCreate?: (
    name: string,
    whitelist: string[],
    draft: OnlinePricingState
  ) => Promise<boolean>;
  creatingProfile?: boolean;
  onNewRangeVisibleChange?: (visible: boolean) => void;
  country?: KlearlyCountryAlpha2Code | string;
  showVendor?: boolean;
  // A save the backend refused. Shown whole, and pinned to the groups it names.
  saveError?: { message?: string; code?: string };
  // Whether the selected range is used by a merchant submitted to Adyen. Read
  // off the partner-details response, never derived here; undefined until that
  // read lands, which reads as frozen — what the step showed before it.
  hasMerchantsSubmittedToAdyen?: boolean;
}

const TEMPLATE_DESCRIPTION: Record<OnlinePricingTemplate, string> = {
  [OnlinePricingTemplate.Default]:
    "Netherlands market - iDEAL plus card brands. Standard online setup for NL merchants.",
  [OnlinePricingTemplate.BancontactBE]:
    "Belgium market - Bancontact plus card brands. Standard online setup for BE merchants.",
  [OnlinePricingTemplate.CardsOnly]:
    "Cards only - no local payment method for this country.",
};

const cloneOnlinePricingState = (
  state: OnlinePricingState
): OnlinePricingState => ({
  ...state,
  buckets: Object.fromEntries(
    Object.entries(state.buckets).map(([key, bucket]) => [key, { ...bucket }])
  ) as OnlinePricingState["buckets"],
  profileWhitelist: state.profileWhitelist
    ? [...state.profileWhitelist]
    : undefined,
  extraProfiles: state.extraProfiles ? [...state.extraProfiles] : undefined,
});

export default function PartnerOnlinePricingStep({
  value,
  onChange,
  mode = "create",
  initial,
  profiles,
  onProfileSelect,
  onProfileCreate,
  creatingProfile,
  onNewRangeVisibleChange,
  country,
  showVendor = true,
  saveError,
  hasMerchantsSubmittedToAdyen,
}: Props) {
  const { token } = theme.useToken();
  const [newRangeVisible, setNewRangeVisible] = useState(false);
  const [rangeName, setRangeName] = useState("");
  const [rangeWhitelist, setRangeWhitelist] = useState("");
  const [rangeDraft, setRangeDraft] = useState<OnlinePricingState>();
  const editingValue = rangeDraft ?? value;

  useEffect(() => {
    onNewRangeVisibleChange?.(newRangeVisible);
    return () => onNewRangeVisibleChange?.(false);
  }, [newRangeVisible, onNewRangeVisibleChange]);

  const errorByKey = useMemo(() => {
    const map: Partial<Record<OnlineGroupKey, OnlineValidationError>> = {};
    validateOnlineState(editingValue).forEach((e) => {
      if (!map[e.key]) map[e.key] = e;
    });
    return map;
  }, [editingValue]);

  const bucketKeys = useMemo(
    () => listedOnlineGroupKeysForState(editingValue),
    [editingValue]
  );

  // The backend names the groups it refused ("... missing a numeric value or
  // valid valueType for: european, amex"), so the rejection can sit on the rows
  // it is about instead of only at the top of the step.
  const rejectedKeys = useMemo(
    () =>
      saveError
        ? new Set(resolveInvalidOnlinePricingBuckets(saveError, bucketKeys))
        : new Set<OnlineGroupKey>(),
    [saveError, bucketKeys]
  );

  const dirtyKeys = useMemo(() => {
    const set = new Set<string>();
    const baseline = rangeDraft ? value : initial;
    if (mode !== "edit" || !baseline) return set;
    bucketKeys.forEach((key) => {
      if (
        JSON.stringify(editingValue.buckets[key]) !==
        JSON.stringify(baseline.buckets[key])
      ) {
        set.add(key);
      }
    });
    return set;
  }, [mode, rangeDraft, value, initial, editingValue, bucketKeys]);

  const updateBucket = (key: string, patch: Partial<InPersonBucketState>) => {
    const next = {
      ...editingValue,
      buckets: {
        ...editingValue.buckets,
        [key]: {
          ...editingValue.buckets[key as OnlineGroupKey],
          ...patch,
        },
      },
    };
    if (rangeDraft) {
      setRangeDraft(next);
      return;
    }
    onChange(next);
  };

  // A range with nothing priced at all has not been filled in yet, so the floor
  // missing on every group is not something the operator has done wrong.
  const unpricedRange = !hasAnyOnlinePricing(editingValue);

  // In edit, only surface validation on buckets the user actually changed, so a
  // legacy partner with out-of-range stored data doesn't open all-red. An
  // unconditional error shows either way, unless the range prices nothing yet.
  const rows: PricingRow[] = bucketKeys.map((key) => {
    const error = errorByKey[key];
    const showError =
      error?.unconditional && unpricedRange
        ? false
        : mode !== "edit" || dirtyKeys.has(key) || error?.unconditional;
    return {
      key,
      label: onlineGroupLabel(key),
      state: editingValue.buckets[key],
      error: rejectedKeys.has(key)
        ? "The save was rejected for this bucket"
        : showError
          ? error?.message
          : undefined,
    };
  });

  const profileOptions = useMemo(() => {
    if (!profiles || profiles.length === 0)
      return [{ value: "default", label: "Default" }];
    return profiles.map((profile) => ({
      value: profile._id,
      label: profile.name || "Default",
    }));
  }, [profiles]);

  const activeProfileId = value.profileId ?? profiles?.[0]?._id ?? "default";

  // A range arriving with no _id is matched to a stored one by name, so reusing
  // a name reads as an edit of that range — and since a new range is 16-bucket
  // by default, reusing a four-bucket range's name comes back 422 as a
  // structure switch. Caught here rather than left to that refusal.
  const duplicateRangeName = useMemo(() => {
    const candidate = rangeName.trim().toLowerCase();
    if (!candidate) return false;
    return (profiles ?? []).some(
      (profile) => (profile.name || "default").toLowerCase() === candidate
    );
  }, [rangeName, profiles]);

  // A range with nothing priced is not a range. On four buckets it serializes to
  // an empty `bucketGroups`, which the backend cannot read a structure off, so it
  // stores a range that prices nothing at all rather than refusing it; on 16 it
  // stores a full set of zero floors. Neither is what the operator meant.
  const rangeDraftUnpriced = !!rangeDraft && unpricedRange;

  // A 16-bucket range is stored whole, so a group left without a floor is saved
  // as Klearly taking nothing on it — commonly the card groups a draft copied
  // from a four-bucket range never carried.
  const rangeDraftUnfloored =
    !!rangeDraft && unflooredOnlineGroups(rangeDraft).length > 0;

  const submitNewRange = async () => {
    const name = rangeName.trim();
    if (
      !name ||
      !rangeDraft ||
      duplicateRangeName ||
      rangeDraftUnpriced ||
      rangeDraftUnfloored
    )
      return;
    const whitelist = Array.from(
      new Set(
        rangeWhitelist
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );
    const created = await onProfileCreate?.(name, whitelist, rangeDraft);
    if (!created) return;
    setRangeName("");
    setRangeWhitelist("");
    setRangeDraft(undefined);
    setNewRangeVisible(false);
  };

  const cancelNewRange = () => {
    setRangeName("");
    setRangeWhitelist("");
    setRangeDraft(undefined);
    setNewRangeVisible(false);
  };

  // Label, description and rows all follow value.pricingTemplate — what is being
  // edited and will be submitted — never re-derived from country, or a partner
  // whose stored template predates this reads as a self-contradiction. The real
  // country-vs-template conflict is still surfaced, by the Alert below.
  const template = editingValue.pricingTemplate;
  const templateDescription =
    TEMPLATE_DESCRIPTION[template] ??
    TEMPLATE_DESCRIPTION[OnlinePricingTemplate.Default];
  const expectedTemplate = onlinePricingTemplateForCountry(country);
  const templateMismatch = expectedTemplate !== template;
  const templateChoice = onlineTemplateChoiceFor(editingValue);
  const templateChoiceLabel = onlineTemplateChoiceLabel(
    templateChoice,
    template
  );

  // A merchant in Adyen is the only thing that freezes a stored range. Until
  // then the backend accepts the move up to 16 buckets, so a range no submitted
  // merchant uses can still be put on another template.
  const rangeFrozen = hasMerchantsSubmittedToAdyen !== false;
  const canChooseTemplate = !!rangeDraft || mode !== "edit" || !rangeFrozen;

  // The move back to four buckets is refused for every partner, so the legacy
  // value is offered only where it is still reachable: a range already stored
  // on it, or a new range, which is the only way to declare one.
  const legacyStructureReachable =
    !!rangeDraft ||
    (initial?.structure ?? editingValue.structure) ===
      OnlinePricingStructure.FourBucket;

  const templateOptions = ONLINE_TEMPLATE_CHOICES.map((choice) => ({
    value: choice,
    label: onlineTemplateChoiceLabel(choice, template),
    disabled:
      choice === OnlineTemplateChoice.Legacy && !legacyStructureReachable,
  }));

  // Only the range on screen moves. A draft range being created holds its own
  // template until it is saved, so it must not write through to the selected one.
  const handleTemplateChange = (next: OnlineTemplateChoice) => {
    const axes = onlineTemplateChoiceAxes(next);
    if (rangeDraft) {
      setRangeDraft(withOnlineTemplateAxes(rangeDraft, axes));
      return;
    }
    onChange(withOnlineTemplateAxes(value, axes));
  };

  // Hint is claimed only once the read says so — undefined is a pending read.
  // Disabled antd Selects swallow native title, so the tooltip sits on a wrapper.
  const lockHint =
    !canChooseTemplate && hasMerchantsSubmittedToAdyen
      ? TEMPLATE_LOCKED_HINT
      : undefined;
  const templateSelect = (
    <Select
      value={templateChoice}
      options={templateOptions}
      onChange={handleTemplateChange}
      disabled={!canChooseTemplate}
      {...(!canChooseTemplate ? { open: false } : {})}
      style={{ width: 280 }}
      aria-label="Pricing template"
    />
  );
  const templateSelector = lockHint ? (
    <Tooltip title={lockHint}>
      <span title={lockHint} style={{ display: "inline-block" }}>
        {templateSelect}
      </span>
    </Tooltip>
  ) : (
    <span>{templateSelect}</span>
  );

  const structureRefusal =
    saveError?.code === STRUCTURE_REFUSAL_CODE ? saveError : undefined;

  // Sits with the selector: both the refusal and the freeze are about which
  // template this range may be priced on, not about any bucket's rate.
  const templateNote = structureRefusal ? (
    <Text
      type="danger"
      style={{ display: "block", fontSize: 12, marginTop: 4, maxWidth: 280 }}
    >
      {structureRefusal.message ??
        "This range cannot be moved between the four- and 16-bucket structures. Declare a separate range for the other structure."}
    </Text>
  ) : lockHint ? (
    <Text
      type="secondary"
      style={{ display: "block", fontSize: 12, marginTop: 4, maxWidth: 280 }}
    >
      {lockHint}
    </Text>
  ) : null;

  const templateInfoBox = (
    <div
      style={{
        flex: 1,
        minWidth: 280,
        display: "flex",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 12,
        background: token.colorPrimaryBg,
        border: `1px solid ${token.colorPrimaryBorder}`,
      }}
    >
      <InfoCircleFilled
        style={{ color: token.colorPrimary, fontSize: 16, marginTop: 2 }}
      />
      <div>
        <Text style={{ fontSize: 13 }}>
          <Text strong style={{ color: token.colorPrimary }}>
            {templateChoiceLabel}
          </Text>{" "}
          - {templateDescription}
        </Text>
        <div
          style={{
            display: "flex",
            gap: 6,
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          {rows.map((row) => (
            <span
              key={row.key}
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorPrimary,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorPrimaryBorder}`,
                borderRadius: 6,
                padding: "2px 8px",
              }}
            >
              {row.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const templateAlerts = (
    <>
      {templateMismatch && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Local payment method doesn't match this partner's country"
          description={`This partner's country requires the "${expectedTemplate}" local payment method, but the pricing shown below is still on "${template}". Saving will be rejected until the underlying partner data is updated to match.`}
        />
      )}
      {saveError && !structureRefusal && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message="Online pricing was not saved"
          description={
            saveError.message ||
            "The server rejected this online pricing. Nothing was saved."
          }
        />
      )}
    </>
  );

  const templateContent = (
    <div role="group" aria-label="Pricing template">
      <Title level={5} style={{ margin: 0 }}>
        Pricing template
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Which online buckets to use for this partner's e-commerce flow.
      </Text>
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "start",
          flexWrap: "wrap",
        }}
      >
        <div>
          {templateSelector}
          {templateNote}
        </div>
        {templateInfoBox}
      </div>
      {templateAlerts}
    </div>
  );

  const creatingRangeView = mode === "edit" && newRangeVisible;
  // A fee typed into a row has to land somewhere. Creating a range sends only
  // the price profiles, and a 16-bucket profile carries no fee of its own.
  const vendorColumn =
    showVendor &&
    (rangeDraft
      ? editingValue.structure !== OnlinePricingStructure.SixteenBucket
      : savesOnlineVendorFee(editingValue));
  // The card rows were seeded from a fee stored per cardholder type, which the
  // first fee save replaces with these per-group values.
  const fourBucketFee = vendorColumn
    ? editingValue.fourBucketVendorFee
    : undefined;
  const fourBucketFeeNote = fourBucketFee && (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message="The SW vendor fee is still stored per card type"
      description={`Stored as ${[
        fourBucketFee.nonCorporateCreditCards &&
          `consumer cards ${formatOnlineFee(fourBucketFee.nonCorporateCreditCards)}`,
        fourBucketFee.corporateCreditCards &&
          `corporate cards ${formatOnlineFee(fourBucketFee.corporateCreditCards)}`,
      ]
        .filter(Boolean)
        .join(
          ", "
        )}. The card rows start from it, with International & commercial on the corporate fee. Saving any SW vendor fee replaces it with the values in this table.`}
    />
  );
  const onlineBucketsContent = (
    <div role="group" aria-label="Online buckets">
      {fourBucketFeeNote}
      <PricingBucketsTable
        title="Online buckets"
        subtitle="Payment links and e-commerce. Same split logic applies."
        rows={rows}
        onChange={updateBucket}
        dirtyKeys={dirtyKeys}
        showVendor={vendorColumn}
        bordered={!creatingRangeView}
      />
    </div>
  );

  return (
    <div style={{ margin: "-4px -24px -16px" }}>
      {mode === "edit" && (
        <div style={{ padding: "20px 24px 16px" }}>
          {newRangeVisible ? (
            <div
              style={{
                border: `1px solid ${token.colorPrimaryBorder}`,
                borderRadius: 12,
                boxShadow: token.boxShadowTertiary,
                overflow: "hidden",
              }}
            >
              <div style={{ padding: 16 }}>
                <Title level={5} style={{ margin: 0 }}>
                  New partner price range
                </Title>
                <Text
                  type="secondary"
                  style={{ display: "block", marginBottom: 12 }}
                >
                  Give the range a name and an optional whitelist. Prices start
                  empty and are saved independently; the structure is this
                  range's own.
                </Text>
                <Form layout="vertical" style={{ margin: 0 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "end" }}>
                    <Form.Item
                      label="Name"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                      validateStatus={duplicateRangeName ? "error" : undefined}
                      help={
                        duplicateRangeName
                          ? "This partner already has a range with that name."
                          : undefined
                      }
                    >
                      <Input
                        value={rangeName}
                        onChange={(event) => setRangeName(event.target.value)}
                        placeholder="e.g. Hospitality"
                      />
                    </Form.Item>
                    <Form.Item
                      label="Whitelist (optional)"
                      style={{ flex: 1.4, marginBottom: 0 }}
                    >
                      <Input
                        value={rangeWhitelist}
                        onChange={(event) =>
                          setRangeWhitelist(event.target.value)
                        }
                        placeholder="Comma-separated merchant KVKs or IDs"
                      />
                    </Form.Item>
                    <Form.Item
                      label="Pricing template"
                      style={{ marginBottom: 0 }}
                    >
                      {templateSelector}
                    </Form.Item>
                    <Button
                      type="primary"
                      onClick={submitNewRange}
                      disabled={
                        !rangeName.trim() ||
                        duplicateRangeName ||
                        rangeDraftUnpriced ||
                        rangeDraftUnfloored
                      }
                      loading={creatingProfile}
                      style={{ width: 84, height: 40 }}
                    >
                      Create
                    </Button>
                    <Button
                      onClick={cancelNewRange}
                      disabled={creatingProfile}
                      style={{ width: 84, height: 40 }}
                    >
                      Cancel
                    </Button>
                  </div>
                  {(rangeDraftUnpriced || rangeDraftUnfloored) && (
                    <Text
                      type="secondary"
                      style={{ display: "block", marginTop: 8 }}
                    >
                      {rangeDraftUnpriced
                        ? "Price at least one bucket below before creating the range."
                        : "Give every highlighted bucket a Klearly floor before creating the range."}
                    </Text>
                  )}
                </Form>
              </div>
              <div style={{ padding: 16 }}>
                {templateInfoBox}
                {templateAlerts}
              </div>
              {onlineBucketsContent}
            </div>
          ) : (
            <div role="group" aria-label="Partner price range">
              <Title level={5} style={{ margin: 0 }}>
                Partner price range
              </Title>
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 12 }}
              >
                The values below configure the selected range. Switch to view
                another, or create a new one.
              </Text>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Select
                  value={activeProfileId}
                  options={profileOptions}
                  onChange={onProfileSelect}
                  style={{ width: 280 }}
                  disabled={!onProfileSelect || profileOptions.length <= 1}
                />
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setRangeDraft({
                      ...cloneOnlinePricingState(value),
                      structure: NEW_RANGE_STRUCTURE,
                      buckets: blankOnlineBuckets(),
                    });
                    setNewRangeVisible(true);
                  }}
                >
                  New
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {!creatingRangeView && (
        <div style={{ padding: "20px 24px 16px" }}>{templateContent}</div>
      )}
      {!creatingRangeView && onlineBucketsContent}
    </div>
  );
}
